/**
 * Poll / Survey / Trivia Infrastructure Types
 *
 * Layer 1 of the Poll Game Architecture.
 * These types are SHARED across ALL game types that leverage polling infrastructure:
 * poll game, future trivia game, Family Feud, Who Wants to Be a Millionaire, etc.
 *
 * Firestore schema:
 *   Poll definitions:   polls/{clientId}/definitions/{pollId}
 *   Session responses:  sessions/{sessionId}/pollResponses/{questionId}
 *   Aggregates:         polls/{clientId}/aggregates/{pollId}/questions/{questionId}
 */

// ─── Poll Definition ─────────────────────────────────────────────────────────

/**
 * A single selectable answer option within a poll question.
 * The `isCorrect` field is used in trivia/quiz reward modes; unused in 'always' mode.
 */
export interface PollOption {
  /** Stable unique ID — used for tallying and response tracking across all submissions */
  id: string;

  /** Display text shown to the user */
  text: string;

  /** Optional supporting image URL displayed alongside the text */
  imageUrl?: string;

  /**
   * Whether this option is the correct answer.
   * Used when campaign rewardMode is 'on-correct' or 'on-score-threshold'.
   * Ignored (and never exposed to users) when rewardMode is 'always'.
   */
  isCorrect?: boolean;
}

/**
 * A single question in a poll, survey, or trivia game.
 */
export interface PollQuestion {
  /** Stable unique ID */
  id: string;

  /** The question text displayed to the user */
  text: string;

  /** The selectable answer options (2–6 supported in the UI; data model is unconstrained) */
  options: PollOption[];

  /**
   * Whether the user can select multiple options simultaneously.
   *
   * DESIGN NOTE: Currently only single-select (false) is implemented in the UI.
   * The data model stores `selectedOptionIds: string[]` from day one —
   * enabling multi-select in the future requires only a UI change, zero data migration.
   *
   * @default false
   */
  allowMultiple?: boolean;

  /**
   * Optional image displayed above the question text (e.g., photo for trivia).
   */
  imageUrl?: string;

  /**
   * Optional explanation shown after the user submits their answer.
   * Useful for trivia/educational contexts ("The correct answer is X because...").
   * Only displayed when `showResultsAfterSubmit` is true in campaign settings.
   */
  explanation?: string;
}

/**
 * A complete poll definition stored in the Poll Library.
 * One definition can be referenced by multiple campaigns/short codes via `pollId`.
 *
 * Firestore path: polls/{clientId}/definitions/{pollId}
 */
export interface PollDefinition {
  id: string;

  /** Client that owns this poll definition */
  clientId: string;

  /** Admin-facing display name (e.g., "Spring 2026 Customer Preferences") */
  name: string;

  /** Optional description shown in the Poll Library */
  description?: string;

  /** Ordered array of questions. Randomisation per-session is handled by the renderer. */
  questions: PollQuestion[];

  /** Tags for categorisation and search in the Poll Library */
  tags?: string[];

  isActive: boolean;

  /** Timestamp — uses `unknown` to support both client (Timestamp) and server (FieldValue) SDKs */
  createdAt?: unknown;
  updatedAt?: unknown;
  createdBy?: string;
}

// ─── Reward Mode ─────────────────────────────────────────────────────────────

/**
 * Determines when a user earns a prize after completing a poll.
 *
 * - `always`:              Everyone who submits all answers wins (participation reward — pure poll mode)
 * - `on-correct`:          User must answer ALL questions correctly
 * - `on-score-threshold`:  User must score >= `scoreThreshold`% correct
 */
export type PollRewardMode = 'always' | 'on-correct' | 'on-score-threshold';

// ─── Session Response Types ────────────────────────────────────────────────────

/**
 * The recorded response for a single question within a session.
 * Stored in: sessions/{sessionId}/pollResponses/{questionId}
 */
export interface PollQuestionResponse {
  questionId: string;

  /**
   * The IDs of the selected options.
   * Always an array — single-select questions will have exactly one entry,
   * but the schema supports future multi-select with no migration.
   */
  selectedOptionIds: string[];

  /**
   * Whether the user's selection was correct.
   * Evaluated server-side for `on-correct` / `on-score-threshold` modes.
   * Undefined for `always` mode (correctness not relevant).
   */
  isCorrect?: boolean;

  /** Timestamp when this response was submitted */
  submittedAt?: unknown;
}

/**
 * Full payload submitted to the `submitPollResponse` Cloud Function.
 */
export interface PollSessionResponse {
  sessionId: string;

  /** Short code for multi-tenant database routing — REQUIRED */
  short: string;

  /** The poll definition ID being answered */
  pollId: string;

  /** All question responses collected during this session */
  responses: PollQuestionResponse[];
}

// ─── Aggregate Types ──────────────────────────────────────────────────────────

/**
 * Live tally for one option within a question.
 */
export interface PollOptionTally {
  optionId: string;
  optionText: string;

  /** Absolute response count */
  count: number;

  /** Percentage of total responses (0–100), computed server-side */
  percentage: number;
}

/**
 * Aggregated response tally for a single question.
 */
export interface PollQuestionTally {
  questionId: string;
  questionText: string;
  totalResponses: number;
  options: PollOptionTally[];
}

/**
 * Complete aggregate response for an entire poll.
 * Returned by the `getPollAggregates` Cloud Function.
 * Also embedded in `SubmitPollResponseResult` when `showResultsAfterSubmit` is true.
 */
export interface PollAggregates {
  pollId: string;
  totalResponses: number;
  questions: PollQuestionTally[];
  lastUpdated?: unknown;
}

// ─── Cloud Function Result ────────────────────────────────────────────────────

/**
 * Result returned by the `submitPollResponse` Cloud Function.
 */
export interface SubmitPollResponseResult {
  success: boolean;

  /**
   * Whether this session has earned a prize based on the campaign's `rewardMode`.
   * When true, the game engine proceeds to call `getPrizeIndex()`.
   */
  prizeEligible: boolean;

  /** Number of correctly answered questions; populated for scoring modes */
  correctCount?: number;

  /** Total questions in this session */
  totalQuestions?: number;

  /**
   * Score as a percentage (0–100).
   * Populated for `on-correct` and `on-score-threshold` modes.
   */
  score?: number;

  /**
   * Live aggregate tallies per question.
   * Only included when `showResultsAfterSubmit` is true in campaign settings.
   */
  aggregates?: PollAggregates;
}

// ─── Preview Utility ─────────────────────────────────────────────────────────

/**
 * A ready-to-use sample poll definition for Portal preview mode.
 * Injected into `settings._previewPollDefinition` by the studio editor and
 * preview route components so the renderer has data to display immediately.
 *
 * NOTE: This is a runtime-only preview fixture — it is never stored in Firestore.
 */
export const SAMPLE_POLL_DEFINITION: PollDefinition = {
  id: 'preview',
  clientId: 'preview',
  name: 'Sample Poll (Preview)',
  description: 'A sample poll for preview purposes',
  isActive: true,
  questions: [
    {
      id: 'q1',
      text: 'What brings you here today?',
      options: [
        { id: 'q1a', text: 'Just browsing 👀' },
        { id: 'q1b', text: 'Looking for a deal 🏷️' },
        { id: 'q1c', text: 'Referred by a friend 🤝' },
        { id: 'q1d', text: 'First time visiting ⭐' },
      ],
    },
    {
      id: 'q2',
      text: 'How often do you visit us?',
      options: [
        { id: 'q2a', text: 'First time!' },
        { id: 'q2b', text: 'A few times a year' },
        { id: 'q2c', text: 'Monthly' },
        { id: 'q2d', text: 'Weekly or more' },
      ],
    },
  ],
};
